/**
 * The table.
 *
 * Declarative columns rather than hand-written `<tr>`s at every call site —
 * four pages were each rebuilding a Carbon DataTable with its own toolbar, and
 * they had already drifted apart. Sorting reuses the existing hooks in
 * `SortableTable.tsx` unchanged, including the rule that matters most here:
 * **nulls sort last in both directions**, because "unknown" is not "zero" and a
 * failed probe must never sort as though it reported a healthy 0.
 *
 * Accessibility carried over from the hooks: each sortable header is a real
 * `<button>` inside its `<th>`, the `<th>` carries `aria-sort` so the state is
 * announced rather than inferred from a glyph, and the scroll box is focusable
 * so a wide table can be scrolled without a pointer.
 */

import { useMemo, type ReactNode } from 'react';

import {
  useSort,
  useSortedRows,
  type SortDirection,
  type SortState,
} from '../SortableTable';
import { ArrowDownIcon, ArrowUpIcon } from './Icon';

export interface Column<T, K extends string> {
  key: K;
  header: ReactNode;
  /** Right-aligned and tabular-numeral. */
  numeric?: boolean;
  sortable?: boolean;
  width?: string;
  /** What to draw. Defaults to the sort value. */
  render?: (row: T) => ReactNode;
  /** What to sort and search on. Defaults to `row[key]`. */
  value?: (row: T) => unknown;
}

export interface DataTableProps<T, K extends string> {
  rows: readonly T[];
  columns: ReadonlyArray<Column<T, K>>;
  getRowId: (row: T) => string;
  /** Accessible name for the scroll region and the table. */
  label: string;
  initialSortKey: K;
  initialSortDirection?: SortDirection;
  /** Free-text filter, matched against every column's value. */
  search?: string;
  onRowClick?: (row: T) => void;
  /** Marks a row as the one currently open in a detail panel. */
  isRowActive?: (row: T) => boolean;
  /** Shown in place of the table when there are no rows at all. */
  empty?: ReactNode;
  /** Shown when rows exist but the search excluded all of them. */
  emptyFiltered?: ReactNode;
}

function defaultValue<T, K extends string>(row: T, column: Column<T, K>): unknown {
  if (column.value) return column.value(row);
  return (row as Record<string, unknown>)[column.key];
}

export function DataTable<T, K extends string>({
  rows,
  columns,
  getRowId,
  label,
  initialSortKey,
  initialSortDirection = 'asc',
  search = '',
  onRowClick,
  isRowActive,
  empty,
  emptyFiltered,
}: DataTableProps<T, K>) {
  const sort = useSort<K>(initialSortKey, initialSortDirection);

  const byKey = useMemo(() => {
    const map = new Map<K, Column<T, K>>();
    for (const column of columns) map.set(column.key, column);
    return map;
  }, [columns]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      columns.some((column) => {
        const value = defaultValue(row, column);
        return value != null && String(value).toLowerCase().includes(needle);
      }),
    );
  }, [rows, columns, search]);

  const sorted = useSortedRows(filtered, sort, (row, key) => {
    const column = byKey.get(key);
    return column ? defaultValue(row, column) : undefined;
  });

  if (rows.length === 0 && empty) return <>{empty}</>;
  if (sorted.length === 0 && filtered.length === 0 && emptyFiltered) {
    return <>{emptyFiltered}</>;
  }

  const interactive = Boolean(onRowClick);

  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label={label}>
      <table className="data-table">
        <caption className="visually-hidden">{label}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <HeaderCell key={column.key} column={column} sort={sort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const id = getRowId(row);
            const active = isRowActive?.(row) ?? false;
            return (
              <tr
                key={id}
                className={[
                  interactive ? 'data-table__row--clickable' : '',
                  active ? 'data-table__row--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                // A clickable row is a convenience, never the only way in: every
                // such table also renders a real link in its first cell, so this
                // adds no keyboard trap and removes no affordance.
                onClick={interactive ? () => onRowClick?.(row) : undefined}
              >
                {columns.map((column) => (
                  <td key={column.key} className={column.numeric ? 'numeric' : undefined}>
                    {column.render ? column.render(row) : String(defaultValue(row, column) ?? '')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HeaderCell<T, K extends string>({
  column,
  sort,
}: {
  column: Column<T, K>;
  sort: SortState<K>;
}) {
  const sortable = column.sortable ?? true;
  const active = sort.key === column.key;

  if (!sortable) {
    return (
      <th scope="col" className={column.numeric ? 'numeric' : undefined} style={{ width: column.width }}>
        {column.header}
      </th>
    );
  }

  return (
    <th
      scope="col"
      className={column.numeric ? 'numeric' : undefined}
      aria-sort={sort.ariaSort(column.key)}
      style={{ width: column.width }}
    >
      <button type="button" className="sort-button" onClick={() => sort.toggle(column.key)}>
        <span>{column.header}</span>
        <span className="sort-button__arrow" data-inactive={!active} aria-hidden="true">
          {active && sort.direction === 'desc' ? (
            <ArrowDownIcon size={12} />
          ) : (
            <ArrowUpIcon size={12} />
          )}
        </span>
      </button>
    </th>
  );
}
